import React from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Home, ShoppingBasket, ClipboardList, User } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { Avatar } from "./Avatar";

const NAV = [
  { path: "/home", label: "Shop", icon: Home, testid: "nav-home" },
  { path: "/cart", label: "Cart", icon: ShoppingBasket, testid: "nav-cart" },
  { path: "/orders", label: "Orders", icon: ClipboardList, testid: "nav-orders" },
  { path: "/profile", label: "Profile", icon: User, testid: "nav-profile" },
];

export default function CustomerLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { itemCount } = useCart();
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#FCFBF8] pb-24 md:pb-0">
      {/* Desktop top nav */}
      <div className="hidden md:block sticky top-0 z-40 bg-[#FCFBF8]/95 backdrop-blur border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <button
            data-testid="brand-logo"
            onClick={() => navigate("/home")}
            className="flex items-center"
          >
            <img src="/ayome_logo.png" alt="Ayome Groceries" className="h-10 w-auto object-contain" />
          </button>
          <nav className="flex items-center gap-1">
            {NAV.map((n) => {
              const active = location.pathname === n.path;
              return (
                <button
                  key={n.path}
                  data-testid={`desktop-${n.testid}`}
                  onClick={() => navigate(n.path)}
                  className={`relative px-4 py-2 rounded-full text-sm font-semibold transition-colors duration-200 ${
                    active ? "bg-[#1A4331] text-white" : "text-gray-700 hover:bg-[#E8F5E9]"
                  }`}
                >
                  {n.label}
                  {n.path === "/cart" && itemCount > 0 && (
                    <span data-testid="cart-badge" className="absolute -top-1 -right-1 bg-[#228B22] text-white text-[10px] w-5 h-5 rounded-full flex items-center justify-center">
                      {itemCount}
                    </span>
                  )}
                </button>
              );
            })}
            <button
              data-testid="header-profile-avatar"
              onClick={() => navigate("/profile")}
              className="ml-2 transition-transform duration-200 hover:scale-105"
              aria-label="Profile"
            >
              <Avatar user={user} size={36} />
            </button>
          </nav>
        </div>
      </div>

      <Outlet />

      {/* Mobile bottom nav */}
      <nav
        data-testid="mobile-bottom-nav"
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-[0_-8px_30px_rgb(0,0,0,0.04)]"
      >
        <div className="grid grid-cols-4">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = location.pathname === n.path;
            return (
              <button
                key={n.path}
                data-testid={n.testid}
                onClick={() => navigate(n.path)}
                className="relative flex flex-col items-center gap-1 py-3 transition-colors duration-200"
              >
                {n.path === "/profile" ? (
                  <Avatar user={user} size={22} className={active ? "ring-2 ring-[#1A4331]" : ""} />
                ) : (
                  <Icon
                    size={22}
                    className={active ? "text-[#1A4331]" : "text-gray-400"}
                    strokeWidth={active ? 2.4 : 2}
                  />
                )}
                <span className={`text-[11px] font-medium ${active ? "text-[#1A4331]" : "text-gray-400"}`}>
                  {n.label}
                </span>
                {n.path === "/cart" && itemCount > 0 && (
                  <span data-testid="cart-badge-mobile" className="absolute top-1.5 right-1/2 translate-x-4 bg-[#228B22] text-white text-[10px] min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
